import * as React from 'react';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { getPriceText } from '../../utils/global/helper';
import {
  cancelDepositCheckout,
  resendCheckoutLink,
} from '../../services/appointments';
import useStateCooldown from '../../hooks/useStateCooldown';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import useSubscriptionModal from '../../hooks/subscriptions/useSubscriptionModal';
import {
  isAppointmentDepositPaid,
  isAppointmentDepositTerminalCheckoutLoading,
} from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import { Appointment, LoadedUser } from '../../interfaces';
import useGetAppointmentPayments from '../../hooks/appointments/useGetAppointmentPayments';
import {
  CASH_DISCOUNT_APPLIED_LABEL,
  getDepositBaseCents,
  getDepositFeeOnlyCents,
  getDepositPaidExclTaxAmount,
  getMetadataFromPayment,
  getPaymentLineItems,
  getPaymentTaxPaidCents,
  isCardPaymentMethod,
  isPaymentComplete,
} from '../../helpers/payments';
import useCashDiscount from '../../hooks/payments/useCashDiscount';
import PaymentLineItem from './PaymentLineItem';
import { cancelCheckout } from '../../services/payments';
import useCheckPendingPayment from '../../hooks/payments/useCheckPendingPayment';
import useInSplitCheckout from '../../hooks/payments/useInSplitCheckout';
import useDeliveryMethods from '../../hooks/global/useDeliveryMethods';
import { getAllUserServices } from '../../helpers/services';

type Props = {
  action: () => void;
  refreshModal: (appointmentId: string) => void;
  appointment: Appointment;
  useSpecStyle?: boolean;
};

const SPEC_PAID_COLOR = 'var(--status-confirmed-text)';

export default function PaymentSection({
  action,
  refreshModal,
  appointment,
  useSpecStyle,
}: Props) {
  const { user } = useUser<LoadedUser>();
  const terminalReaderId = user?.business?.terminalReaderId;

  const [resendDisabled, setResendDisabled] = useStateCooldown(15_000);
  const [loading, setLoading] = React.useState(false);

  const { openSubscriptionModal } = useSubscriptionModal();
  const getDeliveryMethods = useDeliveryMethods();
  const canCheckout = useFeatureGuard(Feature.CHECKOUT);
  const isCheckoutDisabled =
    !appointment ||
    appointment.status === 'CANCELED' ||
    appointment.status === 'ARCHIVED';
  const isInSplitCheckout = useInSplitCheckout();

  const { payments = [], isLoading: isLoadingPayments } =
    useGetAppointmentPayments(appointment);
  useCheckPendingPayment({
    appointment,
    appointmentPayments: payments,
    enabled: !!appointment && !isInSplitCheckout,
  });

  const depositPayment = payments?.find(
    payment => payment.type === 'DEPOSIT' && payment.status === 'SUCCESS',
  );
  const fullPayment = payments?.find(
    payment => payment.type === 'FULL' && payment.status === 'SUCCESS',
  );
  const depositPaymentMetadata = depositPayment
    ? getMetadataFromPayment(depositPayment)
    : null;
  const fullPaymentMetadata = fullPayment
    ? getMetadataFromPayment(fullPayment)
    : null;

  const serviceTaxRate = user?.business?.businessTaxRates?.serviceTaxRate || 0;
  const isDepositPaid = isAppointmentDepositPaid(appointment);

  const isFixedWithPrice =
    !!appointment?.price && appointment.priceType === 'FIXED';

  const isDepositTerminalCheckoutActive =
    isAppointmentDepositTerminalCheckoutLoading(appointment);

  const { getAllInPrice, cashDiscountEnabled: businessCashDiscountEnabled } =
    useCashDiscount();

  // Paid deposits lock to the payment row so later rate changes don't drift
  // Total Due; unpaid projects the deposit's own bundled fee separately from
  // the remaining (each side has its own min-fee floor).
  const allInTotalPrice = React.useMemo(() => {
    if (!isFixedWithPrice) return 0;
    const lockedDepositBaseCents = depositPayment
      ? getDepositBaseCents(depositPayment)
      : undefined;
    const depositPriceForPricing =
      lockedDepositBaseCents != null
        ? lockedDepositBaseCents / 100
        : appointment.depositPrice ?? 0;
    // Only lock when we actually have a value; a 0 here would suppress the
    // projected fee for legacy deposits under a now-CD business.
    const lockedBundledFee = depositPayment
      ? getDepositFeeOnlyCents(depositPayment)
      : 0;
    return getAllInPrice(appointment.price ?? 0, {
      paymentMethod: 'CREDIT',
      depositPrice: depositPriceForPricing,
      depositPaymentMethod:
        depositPayment?.method ?? appointment.depositPaymentMethod ?? 'CREDIT',
      depositBundledFeeCents:
        lockedBundledFee > 0 ? lockedBundledFee : undefined,
    });
  }, [
    isFixedWithPrice,
    appointment.price,
    appointment.depositPrice,
    appointment.depositPaymentMethod,
    depositPayment,
    getAllInPrice,
  ]);

  const truePaidDepositAmount = React.useMemo(() => {
    if (!isDepositPaid) return 0;
    if (!depositPayment) return appointment.depositPrice ?? 0;
    // Non-CD business: fee is a separate line, not bundled — show base only.
    if (!businessCashDiscountEnabled) {
      const baseCents = getDepositBaseCents(depositPayment);
      return baseCents != null
        ? baseCents / 100
        : appointment.depositPrice ?? 0;
    }
    return getDepositPaidExclTaxAmount(depositPayment);
  }, [
    isDepositPaid,
    depositPayment,
    appointment.depositPrice,
    businessCashDiscountEnabled,
  ]);

  const depositCashDiscountApplied = React.useMemo(() => {
    if (!isDepositPaid) return 0;
    if (depositPayment?.cashDiscountEnabled) {
      return Number(depositPaymentMetadata?.cash_discount_cents ?? 0) / 100;
    }
    // Legacy deposit under a now-CD business: only feeless-paid deposits
    // project a would-have-saved fee. Fee-bearing methods already paid it.
    const isFeelessDepositMethod =
      !!depositPayment?.method && !isCardPaymentMethod(depositPayment.method);
    const depositPrice = appointment.depositPrice ?? 0;
    if (!isFeelessDepositMethod || depositPrice <= 0) return 0;
    return (
      getAllInPrice(depositPrice, { paymentMethod: 'CREDIT' }) - depositPrice
    );
  }, [
    isDepositPaid,
    depositPayment,
    depositPaymentMetadata,
    appointment.depositPrice,
    getAllInPrice,
  ]);

  // When the deposit had no tax paid, the Taxes line below only covers the
  // remaining portion — surface Deposit Paid + Cash Discount above it.
  const depositPaidWithoutTax =
    isDepositPaid && (depositPaymentMetadata?.sales_tax ?? 0) === 0;

  const getTotalDue = React.useCallback(
    (depositPaidOverride?: boolean) => {
      if (!appointment || appointment?.checkoutSessionCompleted) return 0;
      let tempTotalDue = isFixedWithPrice ? allInTotalPrice : 0;
      const tempIsDepositPaid = depositPaidOverride ?? isDepositPaid;
      if (tempIsDepositPaid) {
        tempTotalDue -= truePaidDepositAmount + depositCashDiscountApplied;
      }
      return Math.max(0, tempTotalDue);
    },
    [
      appointment,
      isDepositPaid,
      isFixedWithPrice,
      allInTotalPrice,
      truePaidDepositAmount,
      depositCashDiscountApplied,
    ],
  );

  const taxesPaid = React.useMemo(() => {
    const fromLineItem = (
      payment: (typeof payments)[number] | undefined,
      metadata: typeof depositPaymentMetadata,
    ) => {
      if (payment?.lineItems?.length) {
        return getPaymentTaxPaidCents(payment) / 100;
      }
      return (metadata?.sales_tax || 0) / 100;
    };
    return (
      fromLineItem(depositPayment, depositPaymentMetadata) +
      fromLineItem(fullPayment, fullPaymentMetadata)
    );
  }, [
    depositPayment,
    fullPayment,
    depositPaymentMetadata,
    fullPaymentMetadata,
  ]);

  const taxesDue = React.useMemo(() => {
    if (!serviceTaxRate) return 0;

    const due = getTotalDue() * serviceTaxRate;

    return due;
  }, [serviceTaxRate, getTotalDue]);

  const totalDue = React.useMemo(() => {
    const tempTotalDue = getTotalDue();
    if (!tempTotalDue) return 0;
    return tempTotalDue + taxesDue;
  }, [getTotalDue, taxesDue]);

  const cancelAppointmentCheckout = async () => {
    if (!appointment || loading) return;
    try {
      setLoading(true);

      const terminalId =
        appointment.paymentMethod === 'TERMINAL' ? terminalReaderId : undefined;

      const response = isDepositTerminalCheckoutActive
        ? await cancelDepositCheckout(appointment.appointmentId)
        : await cancelCheckout({
            appointmentId: appointment.appointmentId,
            terminalReaderId: terminalId,
          });

      if (response) {
        const successMsg =
          appointment.paymentMethod === 'TERMINAL' ||
          isDepositTerminalCheckoutActive
            ? 'Terminal checkout canceled.'
            : 'Checkout canceled.';
        toast.success(successMsg);
        refreshModal(appointment.appointmentId);
      }
    } catch (cancelError) {
      const errorMsg =
        appointment.paymentMethod === 'TERMINAL'
          ? 'Error canceling terminal checkout'
          : 'Error canceling checkout.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resendPaymentLink = async () => {
    if (loading || !canCheckout || !appointment || isCheckoutDisabled) return;
    try {
      if (
        !appointment.checkoutSessionActive ||
        appointment.checkoutSessionCompleted ||
        appointment.paymentMethod !== 'CREDIT'
      ) {
        return;
      }
      setLoading(true);

      const payload = await getDeliveryMethods({
        customer: appointment.customer,
        title: 'Resend checkout link',
      });
      if (!payload.mediums.length) return;

      const response = await resendCheckoutLink(
        appointment.appointmentId,
        payload,
      );
      if (response) {
        toast.success('Checkout link re-sent.');
        setResendDisabled(true);
      }
    } catch (resendError) {
      toast.error('Error re-sending checkout link');
    } finally {
      setLoading(false);
    }
  };

  const onCheckout = () => {
    if (isCheckoutDisabled) return;
    if (canCheckout) {
      action();
    } else {
      openSubscriptionModal();
    }
  };

  if (!appointment) return null;

  return (
    <>
      {!appointment.checkoutSessionActive &&
        !isDepositTerminalCheckoutActive &&
        !appointment.checkoutSessionCompleted &&
        !appointment.wasNoShow && (
          <div className="payment-section-table">
            <PaymentLineItem
              label="Total Price"
              value={isFixedWithPrice ? allInTotalPrice : 'Add in checkout'}
              useSpecStyle={useSpecStyle}
            />

            {depositPaidWithoutTax && (
              <PaymentLineItem
                label="Deposit Paid"
                value={truePaidDepositAmount}
                subtract
                subtractColor={useSpecStyle ? SPEC_PAID_COLOR : undefined}
                useSpecStyle={useSpecStyle}
              />
            )}

            {depositPaidWithoutTax && depositCashDiscountApplied > 0 && (
              <PaymentLineItem
                label={CASH_DISCOUNT_APPLIED_LABEL}
                value={depositCashDiscountApplied}
                subtract
                subtractColor={useSpecStyle ? SPEC_PAID_COLOR : undefined}
                useSpecStyle={useSpecStyle}
              />
            )}

            {taxesDue + taxesPaid > 0 && (
              <PaymentLineItem
                label="Taxes"
                value={taxesDue + taxesPaid}
                subtract={false}
                useSpecStyle={useSpecStyle}
              />
            )}

            {!depositPaidWithoutTax && isDepositPaid && (
              <PaymentLineItem
                label="Deposit Paid"
                value={truePaidDepositAmount}
                subtract
                subtractColor={useSpecStyle ? SPEC_PAID_COLOR : undefined}
                useSpecStyle={useSpecStyle}
              />
            )}

            {!depositPaidWithoutTax && depositCashDiscountApplied > 0 && (
              <PaymentLineItem
                label={CASH_DISCOUNT_APPLIED_LABEL}
                value={depositCashDiscountApplied}
                subtract
                subtractColor={useSpecStyle ? SPEC_PAID_COLOR : undefined}
                useSpecStyle={useSpecStyle}
              />
            )}

            {!!taxesPaid && (
              <PaymentLineItem
                label="Taxes Paid"
                value={taxesPaid}
                subtract
                subtractColor={useSpecStyle ? SPEC_PAID_COLOR : undefined}
                useSpecStyle={useSpecStyle}
              />
            )}

            <PaymentLineItem
              label="Total Due"
              value={totalDue || 0}
              isTotal
              useSpecStyle={useSpecStyle}
            />

            {!isFixedWithPrice && (
              <PaymentLineItem
                label="Quote"
                value={getPriceText(
                  appointment.priceType!,
                  getAllInPrice(appointment.minPrice ?? 0, {
                    paymentMethod: 'CREDIT',
                  }),
                  getAllInPrice(appointment.maxPrice ?? 0, {
                    paymentMethod: 'CREDIT',
                  }),
                  getAllInPrice(appointment.hourlyPrice ?? 0, {
                    paymentMethod: 'CREDIT',
                  }),
                  getAllInPrice(appointment.price ?? 0, {
                    paymentMethod: 'CREDIT',
                  }),
                )}
                useSpecStyle={useSpecStyle}
              />
            )}
            <Button
              variant="contained"
              onClick={onCheckout}
              className={useSpecStyle ? undefined : 'primary'}
              style={{ width: '100%' }}
              sx={
                useSpecStyle
                  ? {
                      height: 38,
                      borderRadius: '50px',
                      border: '1px solid var(--sidebar-ink)',
                      background: 'var(--sidebar-surface)',
                      boxShadow: 'none',
                      fontSize: 'var(--sidebar-font-base)',
                      fontWeight: 700,
                      color: 'var(--sidebar-ink)',
                      '&:hover': {
                        background: 'var(--sidebar-header-bg)',
                        boxShadow: 'none',
                      },
                    }
                  : undefined
              }
              disabled={isCheckoutDisabled}>
              Checkout
            </Button>
          </div>
        )}

      {!!appointment.checkoutSessionCompleted && (
        <div className="payment-section-table">
          {isLoadingPayments ? (
            <CircularProgress size={24} />
          ) : (
            getPaymentLineItems({
              appointment,
              order:
                payments.filter(isPaymentComplete)?.find(p => p.order)?.order ||
                null,
              payments,
              businessServices: getAllUserServices(user),
              businessCashDiscountEnabled,
            }).map(({ amount, label, isTotal }) => (
              <PaymentLineItem
                key={label}
                label={label}
                value={Math.abs(amount)}
                isTotal={isTotal}
                subtract={amount < 0 || isTotal}
                subtractColor={useSpecStyle ? SPEC_PAID_COLOR : undefined}
                useSpecStyle={useSpecStyle}
              />
            ))
          )}
        </div>
      )}

      {appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.paymentMethod === 'CREDIT' && (
          <div
            className="payment-section-table"
            style={{
              gap: '20px',
            }}>
            <Typography variant="body1" align="center">
              Checkout link sent. Waiting on payment.
            </Typography>
            <CircularProgress />
            {canCheckout && (
              <Box
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                gap={2}
                width="100%">
                <Button
                  variant="text"
                  className="text-btn"
                  onClick={resendPaymentLink}
                  disabled={loading || resendDisabled}>
                  Resend
                </Button>
                <Button
                  variant="text"
                  className="text-btn"
                  onClick={cancelAppointmentCheckout}
                  disabled={loading}>
                  Cancel
                </Button>
              </Box>
            )}
          </div>
        )}

      {(isDepositTerminalCheckoutActive ||
        (appointment.checkoutSessionActive &&
          !appointment.checkoutSessionCompleted &&
          appointment.paymentMethod === 'TERMINAL')) && (
        <div
          className="payment-section-table"
          style={{
            gap: '20px',
          }}>
          <Typography variant="body1" align="center">
            {isDepositTerminalCheckoutActive ? 'Deposit c' : 'C'}harge sent to
            payment terminal. Waiting on payment.
          </Typography>
          <Button
            variant="text"
            className="text-btn"
            onClick={cancelAppointmentCheckout}
            disabled={loading}>
            Having issues? Cancel charge on terminal
          </Button>
        </div>
      )}

      {!!appointment.wasNoShow && (
        <div
          className="payment-section-table"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexDirection: 'column',
            gap: '20px',
          }}>
          <Typography variant="body2" align="left">
            Customer marked as no-show
          </Typography>
        </div>
      )}
    </>
  );
}
